/**
 * Saved-view helpers for v2 reports: normalize whatever is stored in
 * `salon_reports.view_config` into a complete, valid ViewConfig, compare
 * views, and derive the run parameters.
 *
 * Normalizing mirrors the API's strict validator (reports_utils/v2/
 * view_config.py) so a view the canvas saves is always accepted: every key
 * present, no unknown keys, whitelisted groups / filters, known metric keys,
 * 1–4 KPIs. Invalid parts fall back to the definition's default view.
 */
import { deepEqual } from "../../../../utils/deepEqual";
import {
  ReportV2Definition,
  RunReportV2Request,
  ViewConfig,
  ViewRange,
  ViewSort,
  VIEW_CONFIG_VERSION,
} from "../../../../types/reportsV2";
import { isMetricKey } from "./metricCatalog";

export const FIXED_DATES_PRESET = "fixed_dates";
export const LABEL_SORT_KEY = "label";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DENSITIES = ["comfortable", "compact"];

type Raw = Record<string, unknown>;

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

const unique = (list: string[]): string[] => Array.from(new Set(list));

export function normalizeRange(
  def: ReportV2Definition,
  raw: unknown,
): ViewRange {
  const r = (raw ?? {}) as Raw;
  if (r.preset === FIXED_DATES_PRESET) {
    const { start, end } = r;
    if (
      typeof start === "string" &&
      typeof end === "string" &&
      ISO_DATE.test(start) &&
      ISO_DATE.test(end) &&
      start <= end
    ) {
      return { preset: FIXED_DATES_PRESET, start, end };
    }
    return { ...def.default_view.range };
  }
  const preset = r.preset as ViewRange["preset"];
  return def.date_range_presets.includes(preset)
    ? { preset }
    : { ...def.default_view.range };
}

/** Only known filters with a non-empty list of values. */
export function cleanFilters(
  def: ReportV2Definition,
  raw: unknown,
): Record<string, string[]> {
  const known = new Set(def.filters.map((f) => f.key));
  const out: Record<string, string[]> = {};
  Object.entries((raw ?? {}) as Raw).forEach(([key, values]) => {
    if (known.has(key) && isStringList(values) && values.length > 0) {
      out[key] = unique(values);
    }
  });
  return out;
}

export function normalizeShownFilters(
  def: ReportV2Definition,
  raw: unknown,
): string[] {
  const known = def.filters.map((f) => f.key);
  const always = def.filters.filter((f) => f.always_shown).map((f) => f.key);
  const listed = isStringList(raw)
    ? raw.filter((k) => known.includes(k))
    : def.default_view.shown_filters;
  return unique([...always, ...listed]);
}

function normalizeMetricList(
  def: ReportV2Definition,
  raw: unknown,
  fallback: string[],
  [min, max]: [number, number],
): string[] {
  const list = isStringList(raw)
    ? unique(raw.filter((k) => isMetricKey(def, k)))
    : [];
  return list.length >= min && list.length <= max ? list : [...fallback];
}

function normalizeSort(def: ReportV2Definition, raw: unknown): ViewSort | null {
  const s = raw as Partial<ViewSort> | null | undefined;
  if (!s || typeof s !== "object") return null;
  const keyOk =
    s.key === LABEL_SORT_KEY ||
    (typeof s.key === "string" && isMetricKey(def, s.key));
  const dirOk = s.dir === "asc" || s.dir === "desc";
  return keyOk && dirOk ? { key: s.key as string, dir: s.dir! } : null;
}

/**
 * Drop a sort whose column isn't shown any more (sorting by a hidden column
 * would look random); the table falls back to its default order. Sorting by
 * the label column is always valid, whatever the grouping.
 */
export function withValidSort(view: ViewConfig): ViewConfig {
  const { sort } = view;
  if (!sort || sort.key === LABEL_SORT_KEY || view.columns.includes(sort.key)) {
    return view;
  }
  return { ...view, sort: null };
}

/** A complete, validator-safe view from a stored (possibly empty) one. */
export function normalizeViewConfig(
  def: ReportV2Definition,
  stored: unknown,
): ViewConfig {
  const raw = (stored && typeof stored === "object" ? stored : {}) as Raw;
  const d = def.default_view;
  const has = (key: string) => key in raw;
  const groupKeys = def.group_bys.map((g) => g.key);
  const groups = isStringList(raw.groups)
    ? unique(raw.groups.filter((g) => groupKeys.includes(g)))
    : [...d.groups];
  const granularity = def.granularities.some((g) => g.key === raw.granularity)
    ? (raw.granularity as ViewConfig["granularity"])
    : d.granularity;
  return withValidSort({
    v: VIEW_CONFIG_VERSION,
    range: has("range") ? normalizeRange(def, raw.range) : { ...d.range },
    filters: cleanFilters(def, has("filters") ? raw.filters : d.filters),
    shown_filters: normalizeShownFilters(def, raw.shown_filters),
    groups,
    granularity,
    columns: normalizeMetricList(def, raw.columns, d.columns, [1, Infinity]),
    kpis: normalizeMetricList(def, raw.kpis, d.kpis, [
      def.kpi_limits.min,
      def.kpi_limits.max,
    ]),
    sort: has("sort") ? normalizeSort(def, raw.sort) : d.sort,
    density: DENSITIES.includes(raw.density as string)
      ? (raw.density as ViewConfig["density"])
      : d.density,
  });
}

/** Filter values sorted, so picking A then B equals picking B then A. */
const withSortedFilterValues = (view: ViewConfig): ViewConfig => ({
  ...view,
  filters: Object.fromEntries(
    Object.entries(view.filters).map(([k, values]) => [k, [...values].sort()]),
  ),
});

/** Same view? Ignores the order filter values were picked in. */
export const viewsEqual = (a: ViewConfig, b: ViewConfig): boolean =>
  deepEqual(withSortedFilterValues(a), withSortedFilterValues(b));

/**
 * The parts of a view that change the query. Columns, KPIs, sort and density
 * are applied client-side, so they never trigger a refetch.
 */
export function runRequestOf(view: ViewConfig): Required<RunReportV2Request> {
  return {
    range: view.range,
    filters: view.filters,
    groups: view.groups,
    granularity: view.granularity,
  };
}

/** Stable key for a run request (any layout): equal keys ⇒ same query.
 *  Ignores the order filter values were picked in. */
export function requestKeyOf({
  range,
  filters = {},
  groups,
  granularity,
}: RunReportV2Request): string {
  const sortedFilters = Object.keys(filters)
    .sort()
    .map((k) => [k, [...filters[k]].sort()]);
  return JSON.stringify([range, sortedFilters, groups, granularity]);
}

/** Stable key for `runRequestOf(view)`: equal keys ⇒ same query. */
export const runKeyOf = (view: ViewConfig): string =>
  requestKeyOf(runRequestOf(view));
